import React from 'react';
import {
  Box,
  Step,
  StepLabel,
  Stepper,
  useTheme,
  Paper,
  StepButton,
} from '@mui/material';
import { steps } from '../constants/steps';
import { useStepperContext } from '../context/StepperContext';
import { StepperFooter } from '../../shared/components/StepperFooter';
import { stepperStyles } from '../../shared/styles';
import { useLoginContext } from '../../../../contexts/LoginContext';
import { isEqual } from 'lodash';
import ProfileEditPanel from './ProfileEditPanel/ProfileEditPanel';
import { isNotificationProfileValid, isProfileNameValid } from '../../util';

export const DesktopStepper: React.FC = () => {
  const stepperContext = useStepperContext();
  const { activeStep, isNewProfile, setActiveStep } = stepperContext;
  const { user, selectedProfileIndex } = useLoginContext();

  const theme = useTheme();

  const shouldShowStepper =
    user.extProfiles.length > 0 &&
    typeof selectedProfileIndex === 'number' &&
    user.extProfiles[selectedProfileIndex].name !== '' &&
    (isNewProfile
      ? isProfileNameValid(
          user.extProfiles[selectedProfileIndex].name,
          user,
          selectedProfileIndex
        )
      : true);

  const selectedProfile = user?.extProfiles[selectedProfileIndex ?? 0];
  const { originalProfile } = useStepperContext();

  // Deep equality check - treat null originalProfile as equal (no changes to show skip button for)
  const profilesAreEqual =
    originalProfile === null || isEqual(originalProfile, selectedProfile);

  // Check if there are any validation errors across all steps
  const hasValidationErrors =
    undefined !== selectedProfileIndex
      ? !isNotificationProfileValid(user.extProfiles[selectedProfileIndex])
      : true;

  const handleStepClick = (step: number) => {
    setActiveStep(step);
  };

  return (
    <Box sx={stepperStyles.stepperContainer}>
      {/* Fixed header with ProfileSelector */}
      <Paper
        elevation={0}
        sx={{
          flexShrink: 0,
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          borderRadius: 0,
        }}
      >
        <ProfileEditPanel />
      </Paper>

      {/* Fixed stepper navigation and scrollable content area */}
      {shouldShowStepper && (
        <>
          <Paper elevation={0} sx={stepperStyles.stepperHeaderPaper}>
            <Stepper
              activeStep={activeStep}
              sx={stepperStyles.stepperNavigation(theme)}
            >
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepButton onClick={() => setActiveStep(index)}>
                    <StepLabel>{step.label}</StepLabel>
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </Paper>
          {/* Scrollable content area */}
          <Box sx={stepperStyles.stepperContentArea}>
            <Box sx={stepperStyles.stepperContentInner}>
              <Paper
                elevation={1}
                sx={stepperStyles.stepperContentPaper(theme)}
              >
                {steps[activeStep].component &&
                  React.createElement(steps[activeStep].component, {
                    label: steps[activeStep].label,
                    description: steps[activeStep].description,
                    stepIndex: activeStep,
                  })}
              </Paper>
            </Box>
          </Box>
          {/* Fixed footer with navigation buttons */}
          <StepperFooter
            stepperContext={stepperContext}
            showSkipButton={!profilesAreEqual && !hasValidationErrors}
          />
        </>
      )}
    </Box>
  );
};
